import { styled, Tooltip } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { forwardRef } from 'react';
import Icon from 'shell/components/Icon';
import Text from 'shell/components/Text';

const TYPES = ['alert', 'info'];

const CustomTooltip = styled(({ className, $maxWidth, ...rest }) => (
    <Tooltip classes={{ popper: className }} {...rest} />
))(({ $maxWidth: maxWidth, theme }) => {
    return {
        '& .MuiTooltip-tooltip': {
            borderRadius: '8px',
            maxWidth,
            padding: theme.spacing(2),
            background: theme.palette.ensightful.neutral[800],
            color: theme.palette.ensightful.neutral[50],
            boxShadow: theme.palette.ensightful.shadows[1],
        },
        '& .MuiTooltip-arrow': {
            color: theme.palette.ensightful.neutral[800],
        },
    };
});

const AlertIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '50%',
        padding: '6px',
        fontSize: '1.5rem',
        background: theme.palette.ensightful.error[700],
        color: theme.palette.ensightful.shades[0],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const InfoIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '50%',
        padding: '6px',
        fontSize: '1.5rem',
        background: theme.palette.ensightful.primary[500],
        color: theme.palette.ensightful.shades[0],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

/**
 * @param {string} [className]
 * @param {React.ReactNode} [content] - The content of the tooltip
 * @param {string|number} [maxWidth] - The max width of the tooltip
 * @param {'alert'|'info'|string} [type] - The type of tooltip (renders an icon if provided)
 * @param {React.Ref<any>} ref
 */
export default forwardRef(({ className, content, maxWidth, type, ...rest }, ref) => {
    const { device } = useViewport();

    if (!TYPES.includes(type)) {
        return <CustomTooltip ref={ref} className={className} $maxWidth={maxWidth} {...rest} aria-label={undefined} />;
    }

    const variant = {
        mobile: 'p3',
        tablet: 'p3',
        desktop: 'p3',
    }[device];

    return (
        <CustomTooltip
            ref={ref}
            title={
                typeof content === 'string' ? (
                    <Text color={'inherit'} variant={variant}>
                        {content}
                    </Text>
                ) : (
                    content
                )
            }
            $maxWidth={maxWidth}
            {...rest}
        >
            {type === 'info' ? (
                <InfoIcon aria-hidden={false} aria-label={'tooltip'} className={className} tabIndex={0} kind={'info'} />
            ) : (
                <AlertIcon aria-hidden={false} aria-label={'alert'} className={className} tabIndex={0} kind={'error'} />
            )}
        </CustomTooltip>
    );
});
